import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import { X, Save, User, Phone, MapPin, Calendar, FileText, Tag, Briefcase, Heart, Map } from 'lucide-react'
import { api } from '../services/api'

const PersonModal = ({ isOpen, onClose, person = null, onSave, onDelete, canEdit = true }) => {
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    email: '',
    data_nascimento: '',
    genero: '',
    estado_civil: '',
    cidade: '',
    bairro: '',
    estado: '',
    regiao: '',
    endereco: '',
    profissao: '',
    tags: [],
    observacoes: ''
  })
  
  const [loading, setLoading] = useState(false)
  const [profissoes, setProfissoes] = useState([])
  const [tags, setTags] = useState([])
  const [errors, setErrors] = useState({})

  const generos = [
    { value: '', label: 'Selecione...' },
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Feminino' },
    { value: 'O', label: 'Outro' }
  ]

  const estadosCivis = [
    { value: '', label: 'Selecione...' },
    { value: 'solteiro', label: 'Solteiro(a)' },
    { value: 'casado', label: 'Casado(a)' },
    { value: 'divorciado', label: 'Divorciado(a)' },
    { value: 'viuvo', label: 'Viúvo(a)' },
    { value: 'uniao_estavel', label: 'União Estável' }
  ]

  const estados = [
    { value: '', label: 'Selecione...' },
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' }
  ]

  const regioes = [
    { value: '', label: 'Selecione...' },
    { value: 'norte', label: 'Norte' },
    { value: 'nordeste', label: 'Nordeste' },
    { value: 'centro_oeste', label: 'Centro-Oeste' },
    { value: 'sudeste', label: 'Sudeste' },
    { value: 'sul', label: 'Sul' }
  ]

  useEffect(() => {
    if (person) {
      setFormData({
        nome: person.nome || '',
        cpf: person.cpf || '',
        telefone: person.telefone || '',
        email: person.email || '',
        data_nascimento: person.data_nascimento || '',
        genero: person.genero || '',
        estado_civil: person.estado_civil || '',
        cidade: person.cidade || '',
        bairro: person.bairro || '',
        estado: person.estado || '',
        regiao: person.regiao || '',
        endereco: person.endereco || '',
        profissao: person.id_profissao || '',
        tags: person.tags ? person.tags.split(',').map(tag => ({ value: tag.trim(), label: tag.trim() })) : [],
        observacoes: person.observacoes || ''
      })
    } else {
      setFormData({
        nome: '',
        cpf: '',
        telefone: '',
        email: '',
        data_nascimento: '',
        genero: '',
        estado_civil: '',
        cidade: '',
        bairro: '',
        estado: '',
        regiao: '',
        endereco: '',
        profissao: '',
        tags: [],
        observacoes: ''
      })
    }
    setErrors({})
  }, [person])

  useEffect(() => {
    if (isOpen) {
      loadProfissoes()
      loadTags()
    }
  }, [isOpen])

  const loadTags = async () => {
    try {
      const response = await api.get('/tags/list.php');
      if (response.ok && Array.isArray(response.data)) {
        const formattedTags = response.data.map(tag => ({
          value: tag.tag,
          label: tag.tag
        }));
        setTags(formattedTags);
      } else {
        console.error('Erro ao carregar tags:', response.message || 'Formato de dados inválido');
        setTags([]);
      }
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      setTags([]);
    }
  };

  const loadProfissoes = async () => {
    try {
      const response = await api.get('/pessoas/profissoes.php')
      if (response.ok) {
        setProfissoes(response.data || [])
      } else {
        console.error('Erro ao carregar profissões:', response.message)
        // Fallback para dados mockados em caso de erro
        setProfissoes([
          { id_profissao: 1, profissao: 'Advogado' },
          { id_profissao: 2, profissao: 'Médico' },
          { id_profissao: 3, profissao: 'Engenheiro' },
          { id_profissao: 4, profissao: 'Professor' },
          { id_profissao: 5, profissao: 'Administrador' }
        ])
      }
    } catch (error) {
      console.error('Erro ao carregar profissões:', error)
      // Fallback para dados mockados em caso de erro
      setProfissoes([
        { id_profissao: 1, profissao: 'Advogado' },
        { id_profissao: 2, profissao: 'Médico' },
        { id_profissao: 3, profissao: 'Engenheiro' },
        { id_profissao: 4, profissao: 'Professor' },
        { id_profissao: 5, profissao: 'Administrador' }
      ])
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleTagsChange = (selectedOptions) => {
    setFormData(prev => ({ ...prev, tags: selectedOptions }))
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório'
    }
    
    if (formData.cpf && !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido'
    }
    
    if (formData.telefone && !validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido'
    }
    
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Email inválido'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateCPF = (cpf) => {
    const cleanCPF = cpf.replace(/\D/g, '')
    if (cleanCPF.length !== 11) return false
    
    // Validação básica de CPF
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i)
    }
    let remainder = sum % 11
    let digit1 = remainder < 2 ? 0 : 11 - remainder
    
    sum = 0
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i)
    }
    remainder = sum % 11
    let digit2 = remainder < 2 ? 0 : 11 - remainder
    
    return cleanCPF.charAt(9) === digit1.toString() && cleanCPF.charAt(10) === digit2.toString()
  }

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '')
    return cleanPhone.length >= 10 && cleanPhone.length <= 11
  }

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const formatCPF = (value) => {
    const clean = value.replace(/\D/g, '')
    if (clean.length <= 3) return clean
    if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`
    if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`
    return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9, 11)}`
  }

  const formatPhone = (value) => {
    const clean = value.replace(/\D/g, '')
    if (clean.length <= 2) return clean
    if (clean.length <= 7) return `(${clean.slice(0, 2)}) ${clean.slice(2)}`
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7, 11)}`
  }

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value)
    setFormData(prev => ({ ...prev, cpf: formatted }))
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value)
    setFormData(prev => ({ ...prev, telefone: formatted }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setLoading(true)

    const dataToSend = {
      ...formData,
      tags: formData.tags.map(tag => tag.value).join(','),
      id_profissao: formData.profissao
    }

    try {
      await onSave(dataToSend)
      onClose()
    } catch (error) {
      console.error('Erro ao salvar:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!person || !window.confirm('Tem certeza que deseja excluir esta pessoa?')) {
      return
    }
    
    setLoading(true)
    try {
      await onDelete(person.id_pessoa)
      onClose()
    } catch (error) {
      console.error('Erro ao excluir:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {person ? 'Editar Pessoa' : 'Nova Pessoa'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="inline h-4 w-4 mr-2" />
                Nome Completo *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                className={`input w-full ${errors.nome ? 'border-red-500' : ''}`}
                placeholder="Digite o nome completo"
              />
              {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FileText className="inline h-4 w-4 mr-2" />
                CPF
              </label>
              <input
                type="text"
                name="cpf"
                value={formData.cpf}
                onChange={handleCPFChange}
                className={`input w-full ${errors.cpf ? 'border-red-500' : ''}`}
                placeholder="000.000.000-00"
                maxLength="14"
              />
              {errors.cpf && <p className="text-red-500 text-sm mt-1">{errors.cpf}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Phone className="inline h-4 w-4 mr-2" />
                Telefone
              </label>
              <input
                type="text"
                name="telefone"
                value={formData.telefone}
                onChange={handlePhoneChange}
                className={`input w-full ${errors.telefone ? 'border-red-500' : ''}`}
                placeholder="(00) 00000-0000"
                maxLength="15"
              />
              {errors.telefone && <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`input w-full ${errors.email ? 'border-red-500' : ''}`}
                placeholder="email@exemplo.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="inline h-4 w-4 mr-2" />
                Data de Nascimento
              </label>
              <input
                type="date"
                name="data_nascimento"
                value={formData.data_nascimento}
                onChange={handleInputChange}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Heart className="inline h-4 w-4 mr-2" />
                Gênero
              </label>
              <select
                name="genero"
                value={formData.genero}
                onChange={handleInputChange}
                className="input w-full"
              >
                {generos.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado Civil
              </label>
              <select
                name="estado_civil"
                value={formData.estado_civil}
                onChange={handleInputChange}
                className="input w-full"
              >
                {estadosCivis.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Briefcase className="inline h-4 w-4 mr-2" />
                Profissão
              </label>
              <select
                name="profissao"
                value={formData.profissao}
                onChange={handleInputChange}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {profissoes.map(prof => (
                  <option key={prof.id_profissao} value={prof.profissao}>
                    {prof.profissao}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Tag className="inline h-4 w-4 mr-2" />
                Tags
              </label>
              <Select
                isMulti
                name="tags"
                options={tags}
                className="basic-multi-select"
                classNamePrefix="select"
                value={formData.tags}
                onChange={handleTagsChange}
                placeholder="Selecione as tags"
                noOptionsMessage={() => 'Nenhuma tag encontrada'}
              />
            </div>
          </div>

          {/* Endereço */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              <MapPin className="inline h-5 w-5 mr-2" />
              Endereço
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Endereço
                </label>
                <input
                  type="text"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleInputChange}
                  className="input w-full"
                  placeholder="Rua, número, complemento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bairro
                </label>
                <input
                  type="text"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleInputChange}
                  className="input w-full"
                  placeholder="Nome do bairro"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cidade
                </label>
                <input
                  type="text"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleInputChange}
                  className="input w-full"
                  placeholder="Nome da cidade"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estado
                </label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  className="input w-full"
                >
                  {estados.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Map className="inline h-4 w-4 mr-2" />
                  Região
                </label>
                <select
                  name="regiao"
                  value={formData.regiao}
                  onChange={handleInputChange}
                  className="input w-full"
                >
                  {regioes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observações
            </label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              rows="3"
              className="input w-full resize-none"
              placeholder="Informações adicionais..."
            />
          </div>

          {/* Botões */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              {person && canEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  Excluir
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="btn-outline"
              >
                Cancelar
              </button>
              {canEdit && (
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PersonModal
